import React, { useState } from 'react';
import { 
  FaUsers, 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaTimes, 
  FaExclamationTriangle,
  FaCheck,
  FaCrown,
  FaUser,
  FaEye,
  FaEyeSlash
} from 'react-icons/fa';
import useUsers from '../hooks/useUsers';
import useNotification from '../hooks/useNotification';
import { useAuth } from '../contexts/AuthContext';

const UsersPage = () => {
  const { user: currentUser } = useAuth();
  const { users, loading, loadUsers, createUser, updateUser, deleteUser, getRoleName, getRoleColor, getOnlineStatus, getActivityStatusColor } = useUsers();
  const { showNotification } = useNotification();

  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [editingUserIndex, setEditingUserIndex] = useState(-1);
  const [userDeleteConfirmation, setUserDeleteConfirmation] = useState({ open: false, userId: null, user: null });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [userFormData, setUserFormData] = useState({
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    role_id: 3,
    password: '',
    confirmPassword: '',
    isActive: true,
    profileImage: '',
    phoneNumber: '',
    department: '',
    joinDate: new Date().toISOString().split('T')[0]
  });

  const isSuperAdmin = () => currentUser?.role_id === 1;

  const getTimeAgo = (date) => {
    const now = new Date();
    const diffMs = now.getTime() - new Date(date).getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays === 1) return 'Yesterday';
    return `${diffDays} days ago`;
  };

  // User modal handlers
  const openUserModal = (mode = 'add', userId = null) => {
    if (mode === 'edit' && userId) {
      const user = users.find(u => u.id === userId);
      if (user) {
        setUserFormData({
          id: user.id,
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.email || '',
          role_id: user.role_id || 3,
          password: '',
          confirmPassword: '',
          isActive: user.isActive !== undefined ? user.isActive : true,
          profileImage: user.profileImage || '',
          phoneNumber: user.phoneNumber || '',
          department: user.department || '',
          joinDate: user.joinDate || new Date().toISOString().split('T')[0]
        });
        setEditingUserIndex(users.findIndex(u => u.id === userId));
      }
    } else {
      setUserFormData({
        id: '',
        firstName: '',
        lastName: '',
        email: '',
        role_id: 3,
        password: '',
        confirmPassword: '',
        isActive: true,
        profileImage: '',
        phoneNumber: '',
        department: '',
        joinDate: new Date().toISOString().split('T')[0]
      });
      setEditingUserIndex(-1);
    }
    setIsUserModalOpen(true);
  };

  const closeUserModal = () => {
    setIsUserModalOpen(false);
    setEditingUserIndex(-1);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setUserFormData({
      id: '',
      firstName: '',
      lastName: '',
      email: '',
      role_id: 3,
      password: '',
      confirmPassword: '',
      isActive: true,
      profileImage: '',
      phoneNumber: '',
      department: '',
      joinDate: new Date().toISOString().split('T')[0]
    });
  };

  const handleUserSave = async () => {
    // Validation
    if (!userFormData.firstName?.trim()) {
      showNotification('First name is required', 'error');
      return;
    }
    
    if (!userFormData.lastName?.trim()) {
      showNotification('Last name is required', 'error');
      return;
    }
    
    if (!userFormData.email?.trim()) {
      showNotification('Email is required', 'error');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userFormData.email)) {
      showNotification('Please enter a valid email address', 'error');
      return;
    }
    
    const existingUser = users.find(u => u.email === userFormData.email && u.id !== userFormData.id);
    if (existingUser) {
      showNotification('Email address already exists', 'error');
      return;
    }
    
    if (editingUserIndex === -1) {
      if (!userFormData.password?.trim()) {
        showNotification('Password is required for new users', 'error');
        return;
      }
      if (userFormData.password.length < 6) {
        showNotification('Password must be at least 6 characters long', 'error');
        return;
      }
      if (userFormData.password !== userFormData.confirmPassword) {
        showNotification('Passwords do not match', 'error');
        return;
      }
    }
    
    if (editingUserIndex !== -1 && userFormData.password) {
      if (userFormData.password.length < 6) {
        showNotification('Password must be at least 6 characters long', 'error');
        return;
      }
      if (userFormData.password !== userFormData.confirmPassword) {
        showNotification('Passwords do not match', 'error');
        return;
      }
    }

    try {
      const userData = {
        ...userFormData,
        id: userFormData.id || Date.now().toString(),
        lastLogin: editingUserIndex === -1 ? null : users[editingUserIndex]?.lastLogin
      };
      
      delete userData.password;
      delete userData.confirmPassword;
      
      if (editingUserIndex === -1) {
        await createUser(userData);
        showNotification('✅ User created successfully!', 'success');
      } else {
        await updateUser(userData.id, userData);
        showNotification('✅ User updated successfully!', 'success');
      }
      
      // Refresh the users list to show changes
      loadUsers();
      
      closeUserModal();
      
    } catch (error) {
      console.error('Error saving user:', error);
      showNotification(`❌ Failed to save user: ${error.message}`, 'error');
    }
  };

  const handleUserDelete = (userId) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setUserDeleteConfirmation({ 
        open: true, 
        userId, 
        user 
      });
    }
  };

  const confirmUserDelete = async () => {
    const { userId, user } = userDeleteConfirmation;
    if (!userId || !user) return;
    
    if (user.email === 'khouloud.bhlel@gmail.com') {
      showNotification('Cannot delete the super admin account', 'error');
      setUserDeleteConfirmation({ open: false, userId: null, user: null });
      return;
    }
    
    try {
      await deleteUser(userId);
      showNotification(`User "${user.firstName} ${user.lastName}" deleted successfully`, 'success');
      
      // Refresh the users list to show changes
      loadUsers();
      
      setUserDeleteConfirmation({ open: false, userId: null, user: null });
    } catch (error) {
      console.error('Error deleting user:', error);
      showNotification('Failed to delete user: ' + error.message, 'error');
    }
  };

  const cancelUserDelete = () => {
    setUserDeleteConfirmation({ open: false, userId: null, user: null });
  };

  return (
    <div className="users-section">
      <div className="users-header">
        <div className="users-header-content">
          <h2><FaUsers /> User Management</h2>
          <p className="users-subtitle">Manage user accounts, roles, and permissions</p>
        </div>
        <button 
          onClick={() => openUserModal('add')}
          className="add-user-btn"
          disabled={loading}
        >
          <FaPlus /> Add New User
        </button>
      </div>

      {/* User Stats */}
      <div className="user-stats-grid">
        <div className="user-stat-card">
          <div className="user-stat-icon super-admin">
            <FaCrown />
          </div>
          <div className="user-stat-content">
            <div className="user-stat-number">{users.filter(u => u.role_id === 1).length}</div>
            <div className="user-stat-label">Super Admins</div>
          </div>
        </div>
        
        <div className="user-stat-card">
          <div className="user-stat-icon admin">
            <FaUser />
          </div>
          <div className="user-stat-content">
            <div className="user-stat-number">{users.filter(u => u.role_id === 2).length}</div>
            <div className="user-stat-label">Administrators</div>
          </div>
        </div>
        
        <div className="user-stat-card">
          <div className="user-stat-icon user">
            <FaUsers />
          </div>
          <div className="user-stat-content">
            <div className="user-stat-number">{users.filter(u => u.role_id === 3).length}</div>
            <div className="user-stat-label">Regular Users</div>
          </div>
        </div>
        
        <div className="user-stat-card">
          <div className="user-stat-icon active">
            <FaCheck />
          </div>
          <div className="user-stat-content">
            <div className="user-stat-number">{users.filter(u => u.isActive).length}</div>
            <div className="user-stat-label">Active Users</div>
          </div>
        </div>
      </div>

      {/* Users Grid */}
      <div className="users-management-grid">
        {users.map((user) => (
          <div key={user.id} className={`user-management-card ${getRoleColor(user.role_id)}`}>
            <div className="user-card-header">
              <div className="user-avatar-section">
                <div className="user-avatar-large">
                  {user.profileImage ? (
                    <img src={user.profileImage} alt={`${user.firstName} ${user.lastName}`} />
                  ) : (
                    <span className="user-initials">
                      {user.firstName?.[0]}{user.lastName?.[0]}
                    </span>
                  )}
                </div>
                <div className={`user-status-badge ${getActivityStatusColor(user)}`}>
                  <div className={`status-indicator ${getActivityStatusColor(user)}`}></div>
                  {getOnlineStatus(user)}
                </div>
              </div>
              
              <div className="user-actions-dropdown">
                <button 
                  className="user-action-btn edit"
                  onClick={() => openUserModal('edit', user.id)}
                  title="Edit User"
                >
                  <FaEdit />
                </button>
                {user.email !== 'khouloud.bhlel@gmail.com' && (
                  <button 
                    className="user-action-btn delete"
                    onClick={() => handleUserDelete(user.id)}
                    title="Delete User"
                  >
                    <FaTrash />
                  </button>
                )}
              </div>
            </div>
            
            <div className="user-card-body">
              <div className="user-info-section">
                <h3 className="user-full-name">
                  {user.firstName} {user.lastName}
                </h3>
                <p className="user-email">{user.email}</p>
                
                <div className="user-role-badge">
                  <span className={`role-indicator ${getRoleColor(user.role_id)}`}>
                    {user.role_id === 1 && <FaCrown />}
                    {user.role_id === 2 && <FaUser />}
                    {user.role_id === 3 && <FaUser />}
                    {getRoleName(user.role_id)}
                  </span>
                </div>
              </div>
              
              <div className="user-details-section">
                {user.phoneNumber && (
                  <div className="user-detail-item">
                    <span className="detail-label">Phone:</span>
                    <span className="detail-value">{user.phoneNumber}</span>
                  </div>
                )}
                
                {user.department && (
                  <div className="user-detail-item">
                    <span className="detail-label">Department:</span>
                    <span className="detail-value">{user.department}</span>
                  </div>
                )}
                
                <div className="user-detail-item">
                  <span className="detail-label">Joined:</span>
                  <span className="detail-value">
                    {new Date(user.joinDate).toLocaleDateString()}
                  </span>
                </div>
                
                {user.lastLogin && (
                  <div className="user-detail-item">
                    <span className="detail-label">Last Login:</span>
                    <span className="detail-value">
                      {getTimeAgo(user.lastLogin)}
                    </span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="user-card-footer">
              <div className="user-permissions-preview">
                <span className="permissions-label">Permissions:</span>
                <div className="permissions-indicators">
                  {user.role_id === 1 && (
                    <>
                      <span className="permission-badge granted">Full Access</span>
                      <span className="permission-badge granted">User Management</span>
                      <span className="permission-badge granted">Content Management</span>
                    </>
                  )}
                  {user.role_id === 2 && (
                    <>
                      <span className="permission-badge denied">User Management</span>
                      <span className="permission-badge granted">Content Management</span>
                      <span className="permission-badge granted">Analytics</span>
                    </>
                  )}
                  {user.role_id === 3 && (
                    <>
                      <span className="permission-badge denied">User Management</span>
                      <span className="permission-badge denied">Content Management</span>
                      <span className="permission-badge granted">View Only</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Role Permissions Matrix */}
      <div className="role-permissions-matrix">
        <h3><FaCheck /> Role Permissions Matrix</h3>
        <div className="permissions-table-container">
          <table className="permissions-table">
            <thead>
              <tr>
                <th>Permission</th>
                <th>Super Admin</th>
                <th>Administrator</th>
                <th>Regular User</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>User Management</td>
                <td><FaCheck className="permission-granted" /></td>
                <td><FaTimes className="permission-denied" /></td>
                <td><FaTimes className="permission-denied" /></td>
              </tr>
              <tr>
                <td>Proverb CRUD Operations</td>
                <td><FaCheck className="permission-granted" /></td>
                <td><FaCheck className="permission-granted" /></td>
                <td><FaTimes className="permission-denied" /></td>
              </tr>
              <tr>
                <td>Analytics & Reports</td>
                <td><FaCheck className="permission-granted" /></td>
                <td><FaCheck className="permission-granted" /></td>
                <td><FaTimes className="permission-denied" /></td>
              </tr>
              <tr>
                <td>System Configuration</td>
                <td><FaCheck className="permission-granted" /></td>
                <td><FaTimes className="permission-denied" /></td>
                <td><FaTimes className="permission-denied" /></td>
              </tr>
              <tr>
                <td>Export/Import Data</td>
                <td><FaCheck className="permission-granted" /></td>
                <td><FaCheck className="permission-granted" /></td>
                <td><FaTimes className="permission-denied" /></td>
              </tr>
              <tr>
                <td>View Dashboard</td>
                <td><FaCheck className="permission-granted" /></td>
                <td><FaCheck className="permission-granted" /></td>
                <td><FaCheck className="permission-granted" /></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* User Modal */}
      {isUserModalOpen && (
        <div className="modal-overlay" onClick={closeUserModal}>
          <div className="modal-content user-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><FaUsers /> {editingUserIndex === -1 ? 'Add New User' : 'Edit User'}</h3>
              <button onClick={closeUserModal} className="modal-close"><FaTimes /></button>
            </div>
            
            <div className="modal-body">
              <div className="user-form-grid">
                {/* Personal Information Section */}
                <div className="form-section-title"><FaUser /> Personal Information</div>
                
                <div className="form-group required">
                  <label htmlFor="firstName">First Name</label>
                  <input
                    type="text"
                    id="firstName"
                    value={userFormData.firstName}
                    onChange={(e) => setUserFormData({...userFormData, firstName: e.target.value})}
                    placeholder="Enter first name"
                    required
                  />
                </div>
                
                <div className="form-group required">
                  <label htmlFor="lastName">Last Name</label>
                  <input
                    type="text"
                    id="lastName"
                    value={userFormData.lastName}
                    onChange={(e) => setUserFormData({...userFormData, lastName: e.target.value})}
                    placeholder="Enter last name"
                    required
                  />
                </div>
                
                <div className="form-group required full-width">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    value={userFormData.email}
                    onChange={(e) => setUserFormData({...userFormData, email: e.target.value})}
                    placeholder="Enter email address"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="phoneNumber">Phone Number</label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    value={userFormData.phoneNumber}
                    onChange={(e) => setUserFormData({...userFormData, phoneNumber: e.target.value})}
                    placeholder="Enter phone number"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="department">Department</label>
                  <input
                    type="text"
                    id="department"
                    value={userFormData.department}
                    onChange={(e) => setUserFormData({...userFormData, department: e.target.value})}
                    placeholder="Enter department"
                  />
                </div>
                
                {/* Account Settings Section */}
                <div className="form-section-title"><FaCrown /> Account Settings</div>
                
                <div className="form-group">
                  <label htmlFor="role_id">User Role</label>
                  <select
                    id="role_id"
                    value={userFormData.role_id}
                    onChange={(e) => setUserFormData({...userFormData, role_id: parseInt(e.target.value)})}
                    className="role-select"
                  >
                    <option value={3}>Regular User</option>
                    <option value={2}>Administrator</option>
                    {isSuperAdmin() && <option value={1}>Super Administrator</option>}
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="joinDate">Join Date</label>
                  <input
                    type="date"
                    id="joinDate"
                    value={userFormData.joinDate}
                    onChange={(e) => setUserFormData({...userFormData, joinDate: e.target.value})}
                  />
                </div>
                
                <div className="form-group full-width">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={userFormData.isActive}
                      onChange={(e) => setUserFormData({...userFormData, isActive: e.target.checked})}
                    />
                    <span className="checkmark"></span>
                    Account is active
                  </label>
                </div>
                
                {/* Security Section */}
                <div className="form-section-title"><FaCheck /> Security Settings</div>
                
                <div className="form-group">
                  <label htmlFor="password">
                    {editingUserIndex === -1 ? 'Password' : 'New Password (leave empty to keep current)'}
                  </label>
                  <div className="password-input-container">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      value={userFormData.password}
                      onChange={(e) => setUserFormData({...userFormData, password: e.target.value})}
                      placeholder="Enter password"
                      minLength="6"
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowPassword(!showPassword)}
                      title={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <div className="password-input-container">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      value={userFormData.confirmPassword}
                      onChange={(e) => setUserFormData({...userFormData, confirmPassword: e.target.value})}
                      placeholder="Confirm password"
                      minLength="6"
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      title={showConfirmPassword ? "Hide password" : "Show password"}
                    >
                      {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button onClick={closeUserModal} className="cancel-btn" disabled={loading}>
                Cancel
              </button>
              <button onClick={handleUserSave} className="save-btn" disabled={loading}>
                {loading ? 'Saving...' : (editingUserIndex === -1 ? 'Create User' : 'Update User')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Delete Confirmation Modal */}
      {userDeleteConfirmation.open && (
        <div className="modal-overlay" onClick={cancelUserDelete}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header delete-header">
              <h3><FaTrash /> Confirm User Deletion</h3>
              <button onClick={cancelUserDelete} className="modal-close"><FaTimes /></button>
            </div>
            
            <div className="modal-body">
              <div className="delete-confirmation-content">
                <div className="warning-icon"><FaExclamationTriangle /></div>
                <p className="warning-message">
                  Are you sure you want to delete this user account?
                </p>
                {userDeleteConfirmation.user && (
                  <div className="user-preview">
                    <div className="preview-field">
                      <strong>Name:</strong> {userDeleteConfirmation.user.firstName} {userDeleteConfirmation.user.lastName}
                    </div>
                    <div className="preview-field">
                      <strong>Email:</strong> {userDeleteConfirmation.user.email}
                    </div>
                    <div className="preview-field">
                      <strong>Role:</strong> {getRoleName(userDeleteConfirmation.user.role_id)}
                    </div>
                  </div>
                )}
                <p className="delete-warning">
                  This action cannot be undone and will permanently remove the user account and all associated data.
                </p>
              </div>
            </div>
            
            <div className="modal-footer delete-footer">
              <button onClick={cancelUserDelete} className="cancel-btn" disabled={loading}>
                Cancel
              </button>
              <button onClick={confirmUserDelete} className="delete-btn danger" disabled={loading}>
                {loading ? 'Deleting...' : 'Yes, Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
